import type { SentryScopeTags } from '@hub/lib/sentry-scope';

interface SentryScope extends SentryScopeTags {
  setFingerprint(fingerprint: string[]): void;
}

interface ZodLikeIssue {
  path: (string | number)[];
  message: string;
}

function isZodLikeError(error: unknown): error is Error & { issues: ZodLikeIssue[] } {
  if (!(error instanceof Error) || !('issues' in error)) {
    return false;
  }
  const { issues } = error as { issues: unknown };
  if (!Array.isArray(issues) || issues.length === 0) {
    return false;
  }
  const first = issues[0] as Record<string, unknown>;
  return typeof first?.message === 'string' && Array.isArray(first?.path);
}

function summarizeZodIssues(issues: ZodLikeIssue[]): string {
  return issues
    .slice(0, 3)
    .map((i) => {
      const path = i.path.join('.');
      return path ? `${path}: ${i.message}` : i.message;
    })
    .join('; ');
}

export function enrichSentryScope(error: unknown, scope: SentryScope): void {
  if (!isZodLikeError(error)) {
    return;
  }

  const summary = summarizeZodIssues(error.issues);

  const paths = error.issues.map((i) => i.path.join('.')).join(',');

  scope.setTag('error.type', 'ZodError');
  scope.setFingerprint(['zod-validation', paths]);
  scope.setContext('zod', {
    summary,
    issues: error.issues.slice(0, 5),
  });

  if (error.message.startsWith('[')) {
    try {
      Object.defineProperty(error, 'message', {
        value: `Validation failed: ${summary}`,
        writable: true,
        configurable: true,
      });
    } catch {
      // message might not be configurable — safe to skip
    }
  }
}
